import React, { Dispatch, SetStateAction } from 'react';
import { HealthHxItemType, healthHxSort } from '~/util';

export interface MedicationType extends HealthHxItemType {
    id: number;
    daysSupply: number;
    drugName: string;
    fillEndDate: string;
    fillStartDate: string;
    measureCodeAssociation: string;
    quantity: number;
}
interface MeasureAssociationType {
    code: string;
    medications: MedicationType[];
}
export interface PatientContextType {
    measureAssociations: MeasureAssociationType[];
    setMeasureAssociations: Dispatch<SetStateAction<MeasureAssociationType[]>>;
    hasConfirmedMatch: boolean;
    setConfirmedMatch: Dispatch<SetStateAction<boolean>>;
    patientGmpi: number | null;
    setPatientGmpi: Dispatch<SetStateAction<number | null>>;
}

const PatientContext = React.createContext<PatientContextType | null>(null);

function PatientProvider(props: { children: JSX.Element }) {
    const [measureAssociations, setMeasureAssociations] = React.useState<MeasureAssociationType[]>(
        []
    );
    // Eventually this should change to the entire patient object, but starting small
    const [patientGmpi, setPatientGmpi] = React.useState<number | null>(null);

    // used to confirm the right patient is selected for MIC related actions
    const [hasConfirmedMatch, setConfirmedMatch] = React.useState(false);

    const methods = {
        measureAssociations,
        setMeasureAssociations,
        hasConfirmedMatch,
        setConfirmedMatch,
        patientGmpi,
        setPatientGmpi,
    };
    return <PatientContext.Provider value={methods} {...props} />;
}

export { PatientContext, PatientProvider };

export function useStoreMeasureAssociation() {
    const { setMeasureAssociations } = React.useContext(PatientContext) as PatientContextType;
    return React.useCallback(
        (medications: MedicationType[]) => {
            setMeasureAssociations((prev) => [
                ...medications.reduce((acc, med) => {
                    med.measureCodeAssociation?.split(',').forEach((code) => {
                        const existing = acc.find((x) => x.code === code);
                        if (!existing) {
                            acc.push({ code, medications: [med] });
                        } else {
                            existing.medications.push(med);
                        }
                    });
                    return acc;
                }, prev),
            ]);
        },
        [setMeasureAssociations]
    );
}

export function useMeasureAssociation(code: undefined | string) {
    const { measureAssociations } = React.useContext(PatientContext) as PatientContextType;
    return measureAssociations
        .find((x) => x.code === code)
        ?.medications.sort((a, b) => healthHxSort('meds-newest-fillEndDate-drugName', a, b));
}
